import { Box, Typography } from '@mui/material'
import { FC, ReactNode } from 'react'
import { PlayPauseButton } from './PlayPauseButton'
import { formatSeconds } from '../../../../utils/dateUtils'

interface PlayerControlsBarProps {
  isPlaying: boolean
  isBuffering: boolean
  onPlayPause: () => void
  // Shown as a warning on the play/pause button; omit when there's no error to surface (Inspect mode).
  errorMessage?: string
  currentTimeInSeconds: number
  durationInSeconds: number
  // Root testid; the time readout is `${testIdPrefix}-time`. Differs per consumer (Inspect vs QA).
  testIdPrefix: string
  // Trailing controls that vary by mode (clock + panel toggle for Inspect; speed, volumes, download for QA).
  children: ReactNode
}

// The shared pill shell for both player-control bars: the rounded container, play/pause button, and
// current/duration readout. Everything after the readout is mode-specific and comes in via `children`.
// Consumers own their own player state (a raw <video> element in Inspect, an rrweb player in QA) and pass
// the resulting display values down, so this stays presentational.
export const PlayerControlsBar: FC<PlayerControlsBarProps> = ({
  isPlaying,
  isBuffering,
  onPlayPause,
  errorMessage,
  currentTimeInSeconds,
  durationInSeconds,
  testIdPrefix,
  children
}) => {
  return (
    <Box
      sx={{
        borderRadius: 'var(--border-radius-round)',
        bgcolor: 'var(--bg-page)',
        py: 'var(--spacing-2xs)',
        px: 'var(--spacing-s)',
        width: 'auto',
        display: 'inline-flex',
        alignItems: 'center'
      }}
      data-testid={testIdPrefix}
    >
      <PlayPauseButton isPlaying={isPlaying} isBuffering={isBuffering} errorMessage={errorMessage} onClick={onPlayPause} />

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: 'var(--yellow-800)',
          marginRight: 'var(--spacing-l)'
        }}
        data-testid={`${testIdPrefix}-time`}
      >
        {formatSeconds(currentTimeInSeconds)} / {formatSeconds(durationInSeconds)}
      </Typography>

      {children}
    </Box>
  )
}
